import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'antd';

type SyncToPlaybackButtonProps = {
  contentBodyRef: React.RefObject<HTMLDivElement | null>;
  activeTokenElementRef: React.RefObject<HTMLSpanElement | null>;
  isAutoScrolling: boolean;
  setIsAutoScrolling: (value: boolean) => void;
  currentTimeMs: number | null;
};

export default function SyncToPlaybackButton({
  contentBodyRef,
  activeTokenElementRef,
  isAutoScrolling,
  setIsAutoScrolling,
  currentTimeMs
}: SyncToPlaybackButtonProps) {
  const [showSyncButton, setShowSyncButton] = useState(false);

  const getTargetTokenElement = useCallback(() => {
    const container = contentBodyRef.current;
    if (!container) return null;
    if (activeTokenElementRef.current) return activeTokenElementRef.current;
    const readTokens =
      container.querySelectorAll<HTMLSpanElement>('.b-token--read');
    return readTokens.length ? readTokens[readTokens.length - 1] : null;
  }, [activeTokenElementRef, contentBodyRef]);

  const isElementInContainerViewport = useCallback(
    (el: HTMLElement) => {
      const container = contentBodyRef.current;
      if (!container) return true;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offset = 20;
      return (
        elRect.top >= containerRect.top + offset &&
        elRect.bottom <= containerRect.bottom - offset
      );
    },
    [contentBodyRef]
  );

  const updateSyncButtonVisibility = useCallback(() => {
    if (isAutoScrolling) {
      setShowSyncButton(false);
      return;
    }

    const target = getTargetTokenElement();
    if (!target) {
      setShowSyncButton(false);
      return;
    }

    setShowSyncButton(!isElementInContainerViewport(target));
  }, [getTargetTokenElement, isAutoScrolling, isElementInContainerViewport]);

  useEffect(() => {
    const container = contentBodyRef.current;
    if (!container) return;
    const onScroll = updateSyncButtonVisibility;
    container.addEventListener('scroll', onScroll);
    updateSyncButtonVisibility();
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [contentBodyRef, updateSyncButtonVisibility]);

  useEffect(() => {
    updateSyncButtonVisibility();
  }, [currentTimeMs, isAutoScrolling, updateSyncButtonVisibility]);

  const onSyncToPlayback = useCallback(() => {
    const target = getTargetTokenElement();
    if (!target) return;
    setIsAutoScrolling(true);
    setShowSyncButton(false);
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [getTargetTokenElement, setIsAutoScrolling]);

  if (!showSyncButton) return null;

  return (
    <Button
      className="media_viewer_transcription-content_btn"
      onClick={onSyncToPlayback}
    >
      Sync to playback
    </Button>
  );
}
